import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Paper, Popper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UnifiedAnswerRenderer from './UnifiedAnswerRenderer';

const TAG_PREVIEW_VIEWPORT_PADDING = 16;
const TAG_PREVIEW_OFFSET = 4;

const tagPreviewSizeModifier = {
  name: 'tagPreviewSize',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    if (typeof window === 'undefined') {
      return;
    }

    const placement = state.placement.split('-')[0];
    const referenceRect = state.rects.reference;
    const viewportHeight = window.innerHeight;
    let availableHeight = viewportHeight - (TAG_PREVIEW_VIEWPORT_PADDING * 2);

    if (placement === 'bottom') {
      availableHeight = viewportHeight
        - referenceRect.bottom
        - TAG_PREVIEW_VIEWPORT_PADDING
        - TAG_PREVIEW_OFFSET;
    } else if (placement === 'top') {
      availableHeight = referenceRect.top
        - TAG_PREVIEW_VIEWPORT_PADDING
        - TAG_PREVIEW_OFFSET;
    }

    const safeHeight = Math.max(0, Math.floor(availableHeight));
    state.styles.popper = {
      ...state.styles.popper,
      '--tag-preview-max-height': `${safeHeight}px`
    };
  }
};

const TagPreviewContent = ({ tag, loading }) => {
  const { t } = useTranslation();

  if (loading || tag?.loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} sx={{ color: '#5757b0' }} />
        <Typography variant="caption" sx={{ color: '#666' }}>
          {t('tag_editor.loading_preview', 'Loading...')}
        </Typography>
      </Box>
    );
  }

  const content = String(tag?.content || '');
  const label = tag?.label || tag?.tagName;
  const isTimeevent = tag?.tag_type === 'timeevent';
  const isSystemEvent = tag?.tag_type === 'system_event';
  const samples = Array.isArray(tag?.samples) ? tag.samples : [];
  const totalCountRaw = tag?.totalCount;
  const totalCount = Number.isFinite(totalCountRaw) ? totalCountRaw : 0;
  const hasTotal = Number.isFinite(totalCountRaw) && totalCountRaw > 0;

  if ((isTimeevent || isSystemEvent) && samples.length === 0) {
    return (
      <Box sx={{ p: 0, minWidth: 200 }}>
        <Box sx={{
          px: 1.5, py: 0.75,
          borderBottom: '1px solid #eee',
          backgroundColor: 'rgba(87,87,176,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
            {t('tag_editor.event_tag', 'Event Tag')}
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
            {t('tag_editor.no_data', 'No data')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if ((isTimeevent || isSystemEvent) && samples.length > 0) {
    const isSample = hasTotal ? totalCount > samples.length : true;
    return (
      <Box sx={{ p: 0, width: 360 }}>
        <Box sx={{
          px: 1.5, py: 0.75,
          borderBottom: '1px solid #eee',
          backgroundColor: 'rgba(87,87,176,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
            {t('tag_editor.event_tag', 'Event Tag')}
          </Typography>
        </Box>
        {isSample && (
          <Box sx={{ px: 1.5, py: 0.5, backgroundColor: 'rgba(255,152,0,0.08)', borderBottom: '1px solid rgba(255,152,0,0.15)' }}>
            <Typography variant="caption" sx={{ color: '#e65100', fontSize: '0.7rem', fontWeight: 500 }}>
              ⚠ {hasTotal
                ? t('tag_editor.sample_banner', 'Showing {{shown}} of {{total}} events (sample only)', { shown: samples.length, total: totalCount })
                : t('tag_editor.sample_banner_unknown', 'Showing a sample of recent events')}
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 1.5 }}>
          {samples.map((sample, index) => (
            <Box key={`${String(sample)}-${index}`} sx={{
              py: 0.5,
              px: 1,
              mb: 0.5,
              backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
              borderRadius: '4px'
            }}>
              <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                lineHeight: 1.4,
                color: '#333',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {sample}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const isSystem = tag?.tag_type === 'system' || tag?.tag_type === 'system_event';

  if (isSystem) {
    return (
      <Box sx={{ p: 0, minWidth: 200 }}>
        <Box sx={{
          px: 1.5, py: 0.75,
          borderBottom: '1px solid #eee',
          backgroundColor: 'rgba(87,87,176,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
            {isSystemEvent
              ? t('tag_editor.event_tag', 'Event Tag')
              : t('tag_editor.system_tag', 'System Tag')}
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: content ? '#333' : '#999', fontStyle: content ? 'normal' : 'italic' }}>
            {content || t('tag_editor.no_data_yet', 'No data yet')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box sx={{ p: 0, minWidth: 200 }}>
        <Box sx={{
          px: 1.5, py: 0.75,
          borderBottom: '1px solid #eee',
          backgroundColor: 'rgba(87,87,176,0.05)'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
            {t('tag_editor.no_data', 'No data')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, width: 400, maxWidth: 'calc(100vw - 32px)' }}>
      <Box sx={{
        px: 1.5, py: 0.75,
        borderBottom: '1px solid #eee',
        backgroundColor: 'rgba(87,87,176,0.05)'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ minHeight: 100 }}>
        <UnifiedAnswerRenderer
          tag={tag}
          value={content}
          widgetId={`tag-preview-${tag?.tagName}`}
          shouldAutoExpand
        />
      </Box>
    </Box>
  );
};

const TagPreviewPopover = ({
  anchorEl,
  tag,
  loading = false,
  onMouseEnter,
  onMouseLeave
}) => {
  const popperRef = useRef(null);
  const paperRef = useRef(null);

  useEffect(() => {
    if (!anchorEl || !tag || !paperRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      popperRef.current?.update?.();
    });
    resizeObserver.observe(paperRef.current);

    return () => resizeObserver.disconnect();
  }, [anchorEl, tag, loading]);

  return (
    <Popper
      open={Boolean(anchorEl) && Boolean(tag)}
      anchorEl={anchorEl}
      popperRef={popperRef}
      placement="bottom"
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, TAG_PREVIEW_OFFSET] }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'right', 'left'],
            padding: TAG_PREVIEW_VIEWPORT_PADDING
          }
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: TAG_PREVIEW_VIEWPORT_PADDING,
            altAxis: true
          }
        },
        tagPreviewSizeModifier
      ]}
      sx={(theme) => ({
        // Keep the tag preview above action tooltips and other floating surfaces.
        zIndex: (theme.zIndex?.tooltip || 1500) + 1,
        pointerEvents: 'none'
      })}
    >
      <Paper
        ref={paperRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          pointerEvents: 'auto',
          width: 'min(420px, calc(100vw - 32px))',
          maxHeight: 'var(--tag-preview-max-height, calc(100vh - 32px))',
          overflow: 'auto',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          p: 0
        }}
      >
        <TagPreviewContent tag={tag} loading={loading} />
      </Paper>
    </Popper>
  );
};

export default TagPreviewPopover;
